/**
 * Zooz Recharge Subscription Widget upgrade
 * Injects gift timeline + layout styles into the widget shadow root
 * (Recharge Custom CSS can only use ::part — not arbitrary HTML).
 *
 * WARNING: the STYLES block below is a JS template literal. NEVER use a backtick
 * inside it — not even in a CSS comment. One stray backtick closes the string early
 * and the rest of the file is parsed as JavaScript, which kills the whole script
 * silently (the widget still renders, just with none of this applied).
 */
(function () {
  const WIDGET_SELECTOR = 'recharge-subscription-widget';
  const MARKER = 'data-zooz-rc-upgrade';
  const BADGE_MARKER = 'data-zooz-rc-badge';
  const STYLE_ID = 'zooz-rc-upgrade-style';
  const BADGE_SELECTOR = '#rc-subscription-badge, .rc-purchase-option__badge';

  const GIFTS = [
    {
      month: 'Month 1',
      title: 'ZOOZ Events Pass',
      oldPrice: '£35-50',
      image: 'https://cdn.shopify.com/s/files/1/0835/2389/5626/files/gift_1.png',
    },
    {
      month: 'Month 2',
      title: 'Clear Head Club cap',
      oldPrice: '£15',
      image: 'https://cdn.shopify.com/s/files/1/0835/2389/5626/files/gift_3.png',
    },
    {
      month: 'Month 3',
      title: 'Morning Person t-shirt',
      oldPrice: '£20',
      image: 'https://cdn.shopify.com/s/files/1/0835/2389/5626/files/gift_2.png',
    },
  ];

  /**
   * Membership note — "Save X% then Y% on future orders".
   *
   * Scoped to Lime, Pineapple & Mint only; every other product with the widget is
   * left exactly as it was.
   *
   * X is read live from Recharge's own SAVE X% badge, so the sentence can never
   * contradict the badge sitting next to it. Only Y (the future-order rate) is held
   * here — it MUST be kept in step with the recurring discount configured on the
   * matching Recharge plan, or the page will advertise a rate that isn't charged.
   *
   * Keyed by VARIANT id deliberately, not by selling-plan id: Recharge deletes and
   * recreates its Shopify selling plans whenever the plans are edited, so plan ids are
   * not stable. Variant ids are.
   */
  const NOTE_PRODUCT_ID = '15908544610634';
  const FUTURE_DISCOUNTS = {
    '62738181521738': 10, // 8 pack
    '62738181554506': 20, // 16 pack
    '62738181587274': 25, // 24 pack
  };

  const STYLES = `
    .rc-purchase-option {
      box-sizing: border-box;
      max-width: 100%;
      overflow: hidden;
    }

    #rc-subscription-badge,
    .rc-purchase-option__badge {
      position: static !important;
      display: inline-flex !important;
      align-items: center;
      margin: 0 0 0 8px !important;
      white-space: nowrap;
      flex-shrink: 0;
    }

    /* Both options: label + price on one row, inside the card */
    .rc-purchase-option > .rc-purchase-option__label,
    .rc-purchase-option__subscription > .rc-purchase-option__label,
    .rc-purchase-option__onetime > .rc-purchase-option__label,
    .rc-selection__onetime > .rc-purchase-option__label,
    label.rc-purchase-option__label,
    label.rc-purchase-option__label.rc-dynamic-pricing,
    .rc-purchase-option__subscription label.rc-purchase-option__label {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 8px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      min-width: 0;
      padding-top: 12px !important;
      padding-bottom: 12px !important;
    }

    /* Sets the gap between the "Membership" row and the savings line beneath it —
       whatever this is, the gap is. It only needs to clear the was-price, which hangs
       out of flow below the payable price; that price sits ~170px to the right of where
       the savings line ends, so the two can share this vertical band without colliding.
       Same specificity as the padding above, declared later so it wins. */
    .rc-purchase-option__subscription label.rc-purchase-option__label {
      padding-bottom: 12px !important;
    }

    /* overflow must stay visible: hiding it clipped the SAVE badge on mobile. */
    .rc-purchase-option__selector {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      flex-wrap: nowrap;
      gap: 4px 0;
      flex: 1 1 auto;
      min-width: 0;
      overflow: visible;
    }

    .rc-purchase-option__prices {
      display: flex !important;
      flex-direction: column-reverse !important;
      flex-wrap: nowrap !important;
      align-items: flex-end !important;
      justify-content: center !important;
      gap: 0 !important;
      margin-left: auto;
      flex: 0 0 auto;
      min-width: 0;
      box-sizing: border-box;
      position: relative;
    }

    .rc-purchase-option__prices .rc-price {
      white-space: nowrap;
      line-height: 1.2;
    }

    /* Was-price is taken OUT OF FLOW and hung beneath the payable price. In flow the
       prices block was 41px tall (price + was-price) and the row centred that pair,
       which pushed the £28.12 8px above the "Membership" it belongs to. With this out
       of flow the block is the height of the payable price alone, so the row centres
       the two on each other exactly. Size matches the savings line; px not em, since
       the price beside it is much larger. */
    .rc-purchase-option__prices .rc-price.strike-through {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 1px;
      font-size: 13px !important;
      font-weight: 400;
    }

    /* Collapsed membership: hide the was-price. Because it hangs out of flow below the
       payable price, with the card collapsed there is nothing underneath it and it
       dangles past the card's bottom edge. Recharge marks the expanded state with an
       rc-selected attribute AND rc-purchase-option__selected inside part; both are
       checked, so the price only hides when both agree the card is collapsed. */
    .rc-purchase-option__subscription:not([rc-selected]):not([part~="rc-purchase-option__selected"])
      .rc-price.strike-through {
      display: none !important;
    }

    /* Payable price matches the "Membership" / "One-time" headline (20px/600) so the
       number carries the same weight as the option it belongs to. */
    .rc-purchase-option__prices [part~="rc-purchase-option__discounted-price"],
    .rc-purchase-option__prices [part~="rc-purchase-option__price"] {
      font-weight: 700;
      font-size: 20px !important;
      line-height: 1.2;
    }

    @media (max-width: 500px) {
      .rc-purchase-option__badge { margin-left: 4px !important; }
      .rc-purchase-option__prices .rc-price.strike-through { font-size: 12px !important; }
    }

    /* Below ~360px the title, the SAVE badge and a 20px price cannot share one line —
       at 320px the badge and price overlapped by 38px. Let the prices drop to their
       own row rather than shrinking the price this change exists to enlarge.
       NOTE: these selectors deliberately mirror the specificity of the nowrap rule
       above ((0,2,1)); anything lower loses to its !important and silently no-ops. */
    @media (max-width: 360px) {
      .rc-purchase-option label.rc-purchase-option__label,
      .rc-purchase-option__subscription label.rc-purchase-option__label,
      .rc-purchase-option__onetime label.rc-purchase-option__label {
        flex-wrap: wrap !important;
      }

      .rc-purchase-option .rc-purchase-option__prices {
        width: 100% !important;
        margin-left: 0 !important;
        margin-top: 2px;
      }
    }

    rc-benefits,
    .rc-benefits__list,
    #rc-benefits,
    rc-learn-more,
    rc-selling-plans,
    .rc-plans-dropdown,
    .rc-custom-benefits {
      display: none !important;
    }

    /* Sits between the label and the gift list. The 20px inline padding matches the
       label and sub-container, so it shares a left rail with the radio button and the
       gift thumbnails — one clean vertical line down the card. */
    .zooz-sub-note {
      font-size: 13px;
      font-weight: 600;
      line-height: 1.4;
      color: var(--rc-widget-text-color, #171513);
      margin: -2px 0 10px;
      padding: 0 20px;
    }

    .zooz-sub-gifts {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 4px;
      padding-bottom: 4px;
    }

    .zooz-sub-gifts__header {
      font-size: 14px;
      font-weight: 700;
      line-height: 1.3;
      color: var(--rc-widget-text-color, #171513);
    }

    .zooz-sub-gifts__body {
      display: flex;
      flex-direction: column;
      gap: 0;
      background: var(--rc-widget-selected-bg-color, #F9F1E6);
      padding: 8px 0;
    }

    .zooz-sub-gifts__item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 0;
    }

    .zooz-sub-gifts__item:first-child { padding-top: 0; }
    .zooz-sub-gifts__item:last-child { padding-bottom: 0; }

    .zooz-sub-gifts__item:not(:last-child) {
      border-bottom: 1px solid color-mix(in srgb, var(--rc-widget-text-color, #171513) 18%, transparent);
    }

    .zooz-sub-gifts__image {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      background-color: #fff;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    }

    .zooz-sub-gifts__details {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 10px;
      width: calc(100% - 52px);
      min-width: 0;
    }

    .zooz-sub-gifts__copy { min-width: 0; }

    .zooz-sub-gifts__label {
      color: color-mix(in srgb, var(--rc-widget-text-color, #171513) 55%, transparent);
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 2px;
    }

    .zooz-sub-gifts__title {
      font-size: 14px;
      font-weight: 600;
      line-height: 1.3;
      color: var(--rc-widget-text-color, #171513);
    }

    .zooz-sub-gifts__price {
      white-space: nowrap;
      flex-shrink: 0;
      display: flex;
      align-items: baseline;
      gap: 6px;
      font-size: 14px;
      line-height: 1.2;
    }

    .zooz-sub-gifts__old-price {
      color: color-mix(in srgb, var(--rc-widget-text-color, #171513) 55%, transparent);
      text-decoration: line-through;
      font-weight: 400;
    }

    .zooz-sub-gifts__free {
      font-weight: 700;
      color: var(--rc-widget-text-color, #171513);
    }

    .zooz-sub-gifts__footer {
      color: var(--rc-widget-text-color, #171513);
      font-size: 13px;
      line-height: 1.4;
      margin: 0;
    }

    @media (max-width: 500px) {
      .zooz-sub-note { font-size: 12px; }
      .zooz-sub-gifts__item { gap: 8px; }
      .zooz-sub-gifts__image { width: 36px; height: 36px; }
      .zooz-sub-gifts__details { width: calc(100% - 44px); }
      .zooz-sub-gifts__title { font-size: 13px; }
      .zooz-sub-gifts__price { font-size: 13px; }
    }
  `;

  const widgets = new Set();

  function giftsHtml() {
    const items = GIFTS.map(
      (gift) => `
      <div class="zooz-sub-gifts__item">
        <div class="zooz-sub-gifts__image" style="background-image:url('${gift.image}')" role="img" aria-label="${gift.title}"></div>
        <div class="zooz-sub-gifts__details">
          <div class="zooz-sub-gifts__copy">
            <div class="zooz-sub-gifts__label">${gift.month}</div>
            <div class="zooz-sub-gifts__title">${gift.title}</div>
          </div>
          <div class="zooz-sub-gifts__price">
            <span class="zooz-sub-gifts__old-price">${gift.oldPrice}</span>
            <span class="zooz-sub-gifts__free">FREE</span>
          </div>
        </div>
      </div>`
    ).join('');

    return `
      <div class="zooz-sub-gifts" ${MARKER}="gifts">
        <div class="zooz-sub-gifts__body">${items}</div>
        <p class="zooz-sub-gifts__footer">A fresh drop delivered every month • Cancel anytime</p>
      </div>`;
  }

  function ensureStyles(root) {
    if (root.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    root.appendChild(style);
  }

  /**
   * Returns the one badge that should be on screen, having removed any stale copies.
   *
   * Why stale copies happen: we relocate Recharge's badge next to "Membership", which
   * takes it out of the container Recharge's renderer owns. When Recharge re-renders
   * the option — which it does when the selling plans change identity, and Recharge
   * DELETES AND RECREATES its plans every time they are edited — it cannot find the
   * badge it made, so it renders a fresh one and the relocated one is orphaned. The
   * result was two badges side by side showing two different discounts, and because
   * both carry id="rc-subscription-badge", querySelector returned the STALE one, so the
   * savings line quoted the wrong number too.
   *
   * A badge we have already relocated is tagged. Anything untagged is newer than what
   * we are holding, so it wins and our copies are dropped.
   */
  function currentBadge(option) {
    const badges = Array.from(option.querySelectorAll(BADGE_SELECTOR));
    if (!badges.length) return null;

    const untagged = badges.filter((b) => !b.hasAttribute(BADGE_MARKER));
    const current = untagged.length ? untagged[untagged.length - 1] : badges[badges.length - 1];

    badges.forEach((b) => {
      if (b !== current) b.remove();
    });
    current.setAttribute(BADGE_MARKER, '');
    return current;
  }

  function moveBadgeBesideSubscribe(root) {
    const option = root.querySelector('.rc-purchase-option__subscription');
    if (!option) return;

    const badge = currentBadge(option);
    const selector = option.querySelector('.rc-purchase-option__selector');
    if (!badge || !selector) return;

    if (badge.parentElement !== selector) {
      selector.appendChild(badge);
    }
  }

  function subContainer(root) {
    return (
      root.querySelector('.rc-purchase-option__subscription .rc-purchase-option__sub-container') ||
      root.querySelector('[part~="rc-purchase-option__sub-container"]')
    );
  }

  function injectGifts(root) {
    if (root.querySelector(`[${MARKER}="gifts"]`)) return;

    const sub = subContainer(root);
    if (!sub) return;

    const wrap = document.createElement('div');
    wrap.innerHTML = giftsHtml().trim();
    const node = wrap.firstElementChild;
    if (!node) return;

    const first = sub.firstChild;
    if (first) sub.insertBefore(node, first);
    else sub.appendChild(node);
  }

  /**
   * The variant the customer is actually looking at. The widget's own
   * default-variant-id attribute does NOT change when the pack is switched, so read
   * the add-to-cart form instead. Only variants we hold a rate for are accepted,
   * which also keeps other products' forms from matching.
   */
  function currentVariantId() {
    const inputs = document.querySelectorAll('form[action*="/cart/add"] input[name="id"]');
    for (const input of inputs) {
      const value = (input.value || '').trim();
      if (value && Object.prototype.hasOwnProperty.call(FUTURE_DISCOUNTS, value)) return value;
    }
    return null;
  }

  /**
   * Read the live discount off the badge. Takes the deduplicated badge rather than
   * querySelector, which would return the stale duplicate when one exists.
   */
  function badgePercent(option) {
    const badge = currentBadge(option);
    if (!badge) return null;
    const match = badge.textContent.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? match[1] : null;
  }

  function syncNote(widget, root) {
    const option = root.querySelector('.rc-purchase-option__subscription');
    const sub = option ? subContainer(root) : null;
    const existing = option ? option.querySelector(`[${MARKER}="note"]`) : null;

    const applies = widget.getAttribute('product-id') === NOTE_PRODUCT_ID;
    const variantId = applies ? currentVariantId() : null;
    const future = variantId ? FUTURE_DISCOUNTS[variantId] : null;
    const first = applies && option ? badgePercent(option) : null;

    // Render only while the membership option is expanded, and never guess at a
    // number we don't have: different product, unknown variant, or no badge yet.
    if (!option || !sub || future == null || !first) {
      if (existing) existing.remove();
      return;
    }

    const text = `Save ${first}% then ${future}% on future orders`;

    if (!existing) {
      const note = document.createElement('div');
      note.className = 'zooz-sub-note';
      note.setAttribute(MARKER, 'note');
      note.textContent = text;
      // The sub-container is flex column-reverse, so anything placed inside it renders
      // bottom-up. Sit between the label and that container instead — the option itself
      // is plain block flow, which puts this directly under the "Membership" row.
      option.insertBefore(note, sub);
      return;
    }

    // Only write when the text actually changed — this runs from a MutationObserver
    // on the same shadow root, so an unconditional write would retrigger itself.
    if (existing.textContent !== text) existing.textContent = text;
  }

  /**
   * ZOOZ: price per can on the pack tabs.
   *
   * The tabs are rendered by snippets/variant-main-picker.liquid and live in the LIGHT
   * dom; the purchase options live in this shadow root. Both prices are already in the
   * markup — all this does is say which one is showing.
   *
   * Nothing is recalculated here: unlike FUTURE_DISCOUNTS above there is no rate in this
   * block that could drift out of step with Recharge.
   */
  const PER_CAN_SELECTOR = '[data-zooz-percan]';
  const SUB_CLASS = 'zooz-sub-selected';

  /**
   * Recharge marks the chosen option two different ways depending on how it re-rendered:
   * an rc-selected attribute, or rc-purchase-option__selected inside part. Both are
   * accepted — the same pair the stylesheet above tests, kept deliberately in step with
   * it so the tab can never disagree with what the card looks like.
   */
  function subscriptionSelected(root) {
    const option = root.querySelector('.rc-purchase-option__subscription');
    if (!option) return false;
    if (option.hasAttribute('rc-selected')) return true;
    return (option.getAttribute('part') || '').split(/\s+/).includes('rc-purchase-option__selected');
  }

  /**
   * Sets the state and lets CSS do the swap.
   *
   * The class goes on <html> on purpose: the theme re-renders the variant picker on a
   * pack switch, so anything written into those tabs by JS is thrown away and replaced
   * with the server-rendered one-time price. <html> survives that, so the rebuilt markup
   * comes back up already showing the right figure. Writing the text from here instead
   * is what produced the flash of the one-time price when switching packs on Membership.
   */
  function applyPerCan(subscribed) {
    document.documentElement.classList.toggle(SUB_CLASS, subscribed);

    // aria-label only — the visible swap is pure CSS and needs nothing from here. The
    // tab itself is aria-hidden, so the price reaches screen readers through the radio's
    // label, and that has to follow the same state or it announces the wrong price.
    document.querySelectorAll(PER_CAN_SELECTOR).forEach((tab) => {
      const label = tab.closest('label');
      const input = label && label.querySelector('input[data-zooz-percan-aria]');
      if (!input) return;

      const onetime = tab.getAttribute('data-zooz-percan-onetime');
      const sub = tab.getAttribute('data-zooz-percan-sub');

      // Mirrors the CSS: a pack with no membership price stays on its one-time figure.
      const shown = subscribed && sub ? sub : onetime;
      if (!shown) return;

      const aria = `${input.getAttribute('data-zooz-percan-aria')} — ${shown}`;
      if (input.getAttribute('aria-label') !== aria) input.setAttribute('aria-label', aria);
    });
  }

  function syncPerCan(widget) {
    const root = widget.shadowRoot;
    if (!root) return;

    // Only the product the tabs were rendered for. A second widget on the page must not
    // reach across and repaint another product's prices.
    if (widget.getAttribute('product-id') !== NOTE_PRODUCT_ID) return;

    applyPerCan(subscriptionSelected(root));
  }

  /**
   * The observer in watchWidget() is childList-only, so an option flipping from
   * unselected to selected — which is an ATTRIBUTE change — never reaches it. This one
   * watches those attributes, and its callback writes exclusively into the light dom, so
   * unlike enhance() it can never retrigger the shadow-root observer or itself.
   *
   * The change listener is attached to the shadow root rather than the document because
   * change is not composed: it does not cross a shadow boundary, so the document-level
   * listener in init() sees the pack radios but never these purchase-option ones.
   */
  function watchSelection(widget) {
    if (widget.__zoozPerCanObserved || !widget.shadowRoot) return;
    widget.__zoozPerCanObserved = true;

    new MutationObserver(() => syncPerCan(widget)).observe(widget.shadowRoot, {
      attributes: true,
      subtree: true,
      attributeFilter: ['rc-selected', 'part', 'class'],
    });

    widget.shadowRoot.addEventListener('change', () => syncPerCan(widget));

    widget.shadowRoot.addEventListener('click', (event) => {
      // Optimistic: decide from the card that was clicked so the price swaps on the same
      // frame as the click, instead of waiting for Recharge to re-render and mark itself
      // selected. If Recharge lands somewhere else the next tick and the attribute
      // observer both correct it, so a wrong guess costs nothing.
      if (widget.getAttribute('product-id') === NOTE_PRODUCT_ID && event.target.closest) {
        const card = event.target.closest(
          '.rc-purchase-option__subscription, .rc-purchase-option__onetime'
        );
        if (card) applyPerCan(card.classList.contains('rc-purchase-option__subscription'));
      }

      // Recharge sets the selected state after its own click handler returns, so confirm
      // on the next tick rather than mid-flight.
      setTimeout(() => syncPerCan(widget), 0);
    });
  }

  function enhance(widget) {
    const root = widget.shadowRoot;
    if (!root) return false;
    if (!root.querySelector('.rc-purchase-option__subscription')) return false;

    ensureStyles(root);
    moveBadgeBesideSubscribe(root);
    injectGifts(root);
    syncNote(widget, root);
    watchSelection(widget);
    syncPerCan(widget);
    return true;
  }

  function resyncAll() {
    widgets.forEach((widget) => {
      if (widget.shadowRoot) enhance(widget);
    });
  }

  function watchWidget(widget) {
    if (widget.__zoozRcWatched) return;
    widget.__zoozRcWatched = true;
    widgets.add(widget);

    let tries = 0;
    const maxTries = 40;
    const tick = () => {
      if (enhance(widget) || ++tries >= maxTries) return;
      setTimeout(tick, 150);
    };
    tick();

    const attachObserver = () => {
      if (!widget.shadowRoot || widget.__zoozRcObserved) return;
      widget.__zoozRcObserved = true;
      new MutationObserver(() => {
        enhance(widget);
      }).observe(widget.shadowRoot, { childList: true, subtree: true });
    };

    attachObserver();
    if (!widget.shadowRoot) {
      const wait = setInterval(() => {
        if (widget.shadowRoot) {
          clearInterval(wait);
          enhance(widget);
          attachObserver();
        }
      }, 100);
      setTimeout(() => clearInterval(wait), 12000);
    }
  }

  function scan(node) {
    if (!node || node.nodeType !== 1) return;
    const list = node.matches?.(WIDGET_SELECTOR)
      ? [node]
      : Array.from(node.querySelectorAll?.(WIDGET_SELECTOR) || []);
    list.forEach(watchWidget);
  }

  function init() {
    scan(document.documentElement);
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(scan);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

    // Recharge re-renders its badge asynchronously after a pack switch. Clear the note
    // straight away so a stale badge can never be paired with the new pack, then let it
    // come back once the widget has settled.
    document.addEventListener(
      'change',
      (event) => {
        if (!event.target || event.target.type !== 'radio') return;
        widgets.forEach((widget) => {
          const note =
            widget.shadowRoot && widget.shadowRoot.querySelector(`[${MARKER}="note"]`);
          if (note) note.remove();
        });
        [250, 600, 1200].forEach((delay) => setTimeout(resyncAll, delay));
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
